import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  approveLeaveRequest,
  cancelLeaveRequest,
  canApproveLeave,
  createLeaveRequest,
  getLeaveCalendar,
  getLeaveBalances,
  getLeaveHistory,
  getPublicHolidays,
  getTeamLeaveRequests,
  getLeaveTypes,
  LEAVE_STATUS_LABELS,
  rejectLeaveRequest,
  type LeaveBalanceItem,
  type LeaveCalendarItem,
  type LeaveRequestItem,
  type LeaveStatus,
  type LeaveTypeOption,
} from '../api/leave';
import type { LeaveStatus as SharedLeaveStatus } from '@hrmydeck/design-tokens/status';
import { LEAVE_STATUS_VARIANT } from '@hrmydeck/design-tokens/status';
import { useAuth } from '../store/auth';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import PageHeader from '../components/ui/PageHeader';
import { formatDate, formatDateTime, formatDays } from '@/lib/format';
import { cn } from '@/lib/utils';

type StatusFilter = 'ALL' | LeaveStatus;
type ViewTab = 'mine' | 'team';
type LeaveView = 'requests' | 'calendar';
type BalanceMap = Record<string, number>;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function isoDateKey(value: string) {
  return value.slice(0, 10);
}

function monthLabel(month: number, year: number) {
  return formatDate(`${year}-${String(month).padStart(2, '0')}-01`, {
    month: 'long',
    year: 'numeric',
  });
}

function buildMonthDays(month: number, year: number) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const leading = first.getDay();
  const days: Array<{ key: string; label: number; inMonth: boolean }> = [];

  for (let i = leading - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, -i);
    days.push({
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
      label: date.getDate(),
      inMonth: false,
    });
  }

  for (let day = 1; day <= last.getDate(); day++) {
    days.push({
      key: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      label: day,
      inMonth: true,
    });
  }

  while (days.length % 7 !== 0) {
    const previous = days[days.length - 1];
    const date = new Date(`${previous.key}T00:00:00`);
    date.setDate(date.getDate() + 1);
    days.push({
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
      label: date.getDate(),
      inMonth: false,
    });
  }

  return days;
}

function leaveCoversDate(leave: LeaveCalendarItem, dayKey: string) {
  return isoDateKey(leave.startDate) <= dayKey && isoDateKey(leave.endDate) >= dayKey;
}

function LeaveCalendar({
  items,
  month,
  year,
  loading,
  onShiftMonth,
  onSelectDay,
  selectedStart,
  selectedEnd,
  holidays,
}: {
  items: LeaveCalendarItem[];
  month: number;
  year: number;
  loading: boolean;
  onShiftMonth: (delta: number) => void;
  onSelectDay?: (date: string) => void;
  selectedStart?: string;
  selectedEnd?: string;
  /** ISO date (YYYY-MM-DD) → official Malaysian public holiday name. */
  holidays?: Record<string, string>;
}) {
  const days = buildMonthDays(month, year);
  const myLeaveCount = items.filter(l => l.isMine).length;
  const totalLeaveCount = items.length;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-foreground">Leave calendar</h2>
            <p className="text-xs text-muted-foreground">
              {totalLeaveCount} approved this month
              {myLeaveCount > 0 ? ` (${myLeaveCount} yours)` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onShiftMonth(-1)}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[8rem] text-center text-sm font-medium">
              {monthLabel(month, year)}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onShiftMonth(1)}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="py-1">{day}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const dayLeaves = items.filter((leave) => leaveCoversDate(leave, day.key));
            const hasMyLeave = dayLeaves.some(l => l.isMine);
            const hasOthersLeave = dayLeaves.some(l => !l.isMine);
            const holidayName = holidays?.[day.key];
            const isSelected =
              Boolean(selectedStart && selectedEnd) &&
              selectedStart! <= day.key &&
              selectedEnd! >= day.key;

            return (
              <div
                key={day.key}
                role={onSelectDay && day.inMonth ? 'button' : undefined}
                tabIndex={onSelectDay && day.inMonth ? 0 : undefined}
                onClick={() => {
                  if (day.inMonth) onSelectDay?.(day.key);
                }}
                onKeyDown={(event) => {
                  if (!day.inMonth || !onSelectDay) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectDay(day.key);
                  }
                }}
                className={cn(
                  'min-h-20 rounded-lg border p-1.5 text-left',
                  day.inMonth ? 'border-border bg-card' : 'border-border/50 bg-muted/30 text-muted-foreground/60',
                  holidayName && day.inMonth && 'border-warning/40 bg-warning-soft/80',
                  hasMyLeave && 'border-primary/40 bg-primary/5',
                  hasOthersLeave && !hasMyLeave && 'bg-muted/50',
                  isSelected && day.inMonth && 'border-primary bg-primary/10 ring-1 ring-primary/30',
                  onSelectDay && day.inMonth && 'cursor-pointer hover:border-primary/50 hover:bg-primary/5'
                )}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-medium">{day.label}</span>
                  {hasMyLeave && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                </div>
                <div className="mt-1 space-y-1">
                  {holidayName && (
                    <p
                      title={`${holidayName} — public holiday`}
                      className="truncate rounded bg-warning-soft px-1 py-0.5 text-[10px] font-semibold text-warning"
                    >
                      {holidayName}
                    </p>
                  )}
                  {dayLeaves.slice(0, 2).map((leave) => (
                    <p
                      key={leave.id}
                      title={`${leave.name}${leave.leaveLabel ? ` — ${leave.leaveLabel}` : ''}`}
                      className={cn(
                        'truncate rounded px-1 py-0.5 text-[10px] font-medium',
                        leave.isMine
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {leave.name.split(' ')[0]}
                    </p>
                  ))}
                  {dayLeaves.length > 2 && (
                    <p className="text-[10px] text-muted-foreground">+{dayLeaves.length - 2} more</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">Refreshing calendar...</p>
        )}
      </CardContent>
    </Card>
  );
}

function combineDateTime(date: string, time: string) {
  return `${date}T${time || '00:00'}`;
}

export default function LeaveList() {
  const { user } = useAuth();
  const isApprover = user ? canApproveLeave(user.role) : false;
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef<HTMLLIElement>(null);

  const [viewTab, setViewTab] = useState<ViewTab>(isApprover ? 'team' : 'mine');
  const [leaveView, setLeaveView] = useState<LeaveView>('calendar');
  const [list, setList] = useState<LeaveRequestItem[]>([]);
  const [allForCounts, setAllForCounts] = useState<LeaveRequestItem[]>([]);
  const [calendarItems, setCalendarItems] = useState<LeaveCalendarItem[]>([]);
  const [types, setTypes] = useState<LeaveTypeOption[]>([]);
  const [balances, setBalances] = useState<BalanceMap>({});
  const now = new Date();
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [holidays, setHolidays] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(isApprover ? 'PENDING' : 'ALL');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);
  const [showApplyPanel, setShowApplyPanel] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('08:30');
  const [endTime, setEndTime] = useState('16:30');
  const [halfDay, setHalfDay] = useState<'FULL' | 'FIRST_HALF' | 'SECOND_HALF'>('FULL');
  const [reason, setReason] = useState('');
  const [rejectModalId, setRejectModalId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  const load = useCallback(async (status: StatusFilter, tab: ViewTab) => {
    setLoading(true);
    setError('');

    if (tab === 'team') {
      const res = await getTeamLeaveRequests(status === 'ALL' ? undefined : { status });
      if (Array.isArray(res.data)) setList(res.data);
      else if (res.error) {
        setError(res.error);
        setList([]);
      } else setList([]);
    } else {
      const res = await getLeaveHistory({ status, limit: 50, page: 1 });
      if (res.data?.requests) setList(res.data.requests);
      else if (res.error) {
        setError(res.error);
        setList([]);
      } else setList([]);
    }

    setLoading(false);
  }, []);

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true);
    setError('');
    const res = await getLeaveCalendar({ month: calendarMonth, year: calendarYear });
    if (Array.isArray(res.data)) {
      setCalendarItems(res.data);
    } else if (res.error) {
      setError(res.error);
      setCalendarItems([]);
    } else {
      setCalendarItems([]);
    }
    setCalendarLoading(false);
  }, [calendarMonth, calendarYear]);

  const loadHolidays = useCallback(async () => {
    const res = await getPublicHolidays(calendarMonth, calendarYear);
    const map: Record<string, string> = {};
    if (Array.isArray(res.data)) {
      for (const holiday of res.data) map[holiday.date] = holiday.name;
    }
    setHolidays(map);
  }, [calendarMonth, calendarYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    getLeaveHistory({ status: 'ALL', limit: 100, page: 1 }).then((res) => {
      if (res.data?.requests) setAllForCounts(res.data.requests);
    });
  }, []);

  useEffect(() => {
    getLeaveTypes().then((res) => {
      if (!Array.isArray(res.data)) return;
      setTypes(res.data);
      if (!leaveTypeId && res.data[0]) setLeaveTypeId(res.data[0].id);
    });
  }, [leaveTypeId]);

  useEffect(() => {
    if (!user?.employeeId) return;
    getLeaveBalances(user.employeeId, new Date().getFullYear()).then((res) => {
      if (!res.data) return;
      const items = Array.isArray(res.data) ? res.data : (res.data as { data?: LeaveBalanceItem[] }).data ?? [];
      const map: BalanceMap = {};
      for (const item of items) {
        if (item.leaveType?.id) {
          map[item.leaveType.id] = item.remainingDays ?? item.totalAvailable ?? 0;
        }
      }
      setBalances(map);
    });
  }, [user?.employeeId]);

  useEffect(() => {
    load(statusFilter, viewTab);
  }, [statusFilter, viewTab, load]);

  useEffect(() => {
    if (leaveView === 'calendar') {
      loadCalendar();
    }
  }, [leaveView, loadCalendar]);

  useEffect(() => {
    if (!highlightId || loading) return;
    setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, [highlightId, loading, list]);

  const statusCounts = {
    PENDING: allForCounts.filter((r) => r.status === 'PENDING').length,
    APPROVED: allForCounts.filter((r) => r.status === 'APPROVED').length,
    REJECTED: allForCounts.filter((r) => r.status === 'REJECTED').length,
    CANCELLED: allForCounts.filter((r) => r.status === 'CANCELLED').length,
  };

  const handleApprove = async (id: string) => {
    setActionId(id);
    setError('');
    const res = await approveLeaveRequest(id);
    setActionId(null);
    if (res.error) {
      setError(res.error);
      return;
    }
    await load(statusFilter, viewTab);
  };

  const handleReject = (id: string) => {
    setRejectReason('');
    setRejectModalId(id);
  };

  const handleRejectConfirm = async () => {
    if (!rejectModalId || !rejectReason.trim()) return;
    setRejectLoading(true);
    setError('');
    const res = await rejectLeaveRequest(rejectModalId, rejectReason.trim());
    setRejectLoading(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setRejectModalId(null);
    setRejectReason('');
    await load(statusFilter, viewTab);
  };

  const handleCancel = async (id: string) => {
    setActionId(id);
    setError('');
    const res = await cancelLeaveRequest(id);
    setActionId(null);
    if (res.error) {
      setError(res.error);
      return;
    }
    await Promise.all([
      load(statusFilter, viewTab),
      getLeaveHistory({ status: 'ALL', limit: 100, page: 1 }).then((h) => {
        if (h.data?.requests) setAllForCounts(h.data.requests);
      }),
    ]);
  };

  const shiftCalendarMonth = (delta: number) => {
    const next = new Date(calendarYear, calendarMonth - 1 + delta, 1);
    setCalendarMonth(next.getMonth() + 1);
    setCalendarYear(next.getFullYear());
  };

  const openApplyPanel = () => {
    setLeaveView('calendar');
    setViewTab('mine');
    setShowApplyPanel(true);
    setStartDate('');
    setEndDate('');
    setHalfDay('FULL');
    setError('');
  };

  const handleSelectCalendarDay = (date: string) => {
    // Public holidays cannot be covered by leave — refuse to start or extend
    // a selection on one (the backend enforces the same rule on submit).
    const holidayName = holidays[date];
    if (holidayName) {
      setError(`${holidayName} is a public holiday — leave cannot be applied on public holidays.`);
      return;
    }

    // Applying starts from the calendar itself (no Apply button): the first
    // click on a day opens the apply panel with that day pre-selected.
    if (!showApplyPanel) {
      setLeaveView('calendar');
      setViewTab('mine');
      setShowApplyPanel(true);
      setStartDate(date);
      setEndDate(date);
      setHalfDay('FULL');
      setError('');
      return;
    }
    if (!startDate || (startDate && endDate && startDate !== endDate)) {
      setStartDate(date);
      setEndDate(date);
      return;
    }
    if (date < startDate) {
      setEndDate(startDate);
      setStartDate(date);
      return;
    }
    setEndDate(date);
  };

  const handleSubmitLeave = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!leaveTypeId || !startDate || !endDate) {
      setError('Choose leave type, start day, and end day.');
      return;
    }

    if (halfDay !== 'FULL' && startDate !== endDate) {
      setError('Half-day leave is only for a single day.');
      return;
    }

    const start = combineDateTime(startDate, startTime);
    const end = combineDateTime(endDate, endTime);
    if (new Date(end).getTime() < new Date(start).getTime()) {
      setError('End day and time must be after start day and time.');
      return;
    }

    setSubmitting(true);
    const res = await createLeaveRequest({
      leaveTypeId,
      startDate: start,
      endDate: end,
      halfDay,
      reason: reason || undefined,
    });
    setSubmitting(false);

    if (res.error) {
      setError(res.error);
      return;
    }

    setShowApplyPanel(false);
    setReason('');
    setStatusFilter('ALL');
    await Promise.all([
      load('ALL', 'mine'),
      loadCalendar(),
      getLeaveHistory({ status: 'ALL', limit: 100, page: 1 }).then((history) => {
        if (history.data?.requests) setAllForCounts(history.data.requests);
      }),
    ]);
  };

  const pageTitle = viewTab === 'team' ? 'Team leave' : 'My leave';
  const pageSubtitle =
    viewTab === 'team' ? 'Review and approve team requests' : 'Track requests and balances';

  return (
    <>
    {rejectModalId && (
      <div className="fixed inset-0 z-[150] flex items-end justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-t-2xl bg-background p-5 shadow-2xl space-y-4">
          <h3 className="text-base font-semibold">Reject leave request</h3>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Reason (required)</span>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason…"
              className="flex w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              autoFocus
            />
          </label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => setRejectModalId(null)}
              disabled={rejectLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="flex-1"
              onClick={handleRejectConfirm}
              disabled={rejectLoading || !rejectReason.trim()}
            >
              {rejectLoading ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>
        </div>
      </div>
    )}
    <div className="space-y-6">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          viewTab === 'mine' ? (
            <Button size="sm" variant="outline" asChild>
              <Link to="/leave-balance">Balance</Link>
            </Button>
          ) : undefined
        }
      />

      {isApprover && (
        <div className="flex gap-2 rounded-2xl border border-border bg-muted/30 p-1">
          <button
            type="button"
            onClick={() => {
              setViewTab('mine');
              setStatusFilter('ALL');
            }}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
              viewTab === 'mine'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            My leave
          </button>
          <button
            type="button"
            onClick={() => {
              setViewTab('team');
              setStatusFilter('PENDING');
            }}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
              viewTab === 'team'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Team leave
          </button>
        </div>
      )}

      <div className="flex gap-2 rounded-2xl border border-border bg-muted/30 p-1">
        <button
          type="button"
          onClick={() => setLeaveView('calendar')}
          className={cn(
            'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
            leaveView === 'calendar'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Calendar
        </button>
        <button
          type="button"
          onClick={() => setLeaveView('requests')}
          className={cn(
            'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
            leaveView === 'requests'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Requests
        </button>
      </div>

      {leaveView === 'calendar' && (
        <>
          <LeaveCalendar
            items={calendarItems}
            month={calendarMonth}
            year={calendarYear}
            loading={calendarLoading}
            onShiftMonth={shiftCalendarMonth}
            onSelectDay={viewTab === 'mine' ? handleSelectCalendarDay : undefined}
            selectedStart={showApplyPanel ? startDate : undefined}
            selectedEnd={showApplyPanel ? endDate : undefined}
            holidays={holidays}
          />
          {showApplyPanel && (
            <Card>
              <CardContent className="space-y-4 p-4">
                <form onSubmit={handleSubmitLeave} className="space-y-4">
                  <label className="block">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-sm font-medium">Leave type</span>
                      {leaveTypeId && balances[leaveTypeId] !== undefined && (
                        <span className={cn('text-xs font-medium', balances[leaveTypeId] <= 0 ? 'text-destructive' : 'text-muted-foreground')}>
                          {balances[leaveTypeId]} day{balances[leaveTypeId] === 1 ? '' : 's'} remaining
                        </span>
                      )}
                    </div>
                    <select
                      value={leaveTypeId}
                      onChange={(event) => setLeaveTypeId(event.target.value)}
                      className="flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    >
                      {types.map((type) => (
                        <option key={type.id} value={type.id}>{type.displayName}</option>
                      ))}
                    </select>
                  </label>

                  <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Selected leave</p>
                    <p className="mt-1 text-sm font-semibold text-foreground">
                      {startDate && endDate
                        ? startDate === endDate
                          ? formatDate(startDate)
                          : `${formatDate(startDate)} - ${formatDate(endDate)}`
                        : 'No day selected'}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium">Start time</span>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(event) => setStartTime(event.target.value)}
                        className="flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium">End time</span>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(event) => setEndTime(event.target.value)}
                        className="flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        required
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium">Duration</span>
                    <select
                      value={halfDay}
                      onChange={(event) => setHalfDay(event.target.value as 'FULL' | 'FIRST_HALF' | 'SECOND_HALF')}
                      className="flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="FULL">Full day</option>
                      <option value="FIRST_HALF">Half day — first half (AM)</option>
                      <option value="SECOND_HALF">Half day — second half (PM)</option>
                    </select>
                    {halfDay !== 'FULL' && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {halfDay === 'FIRST_HALF' ? '08:30 – 12:30' : '12:30 – 16:30'} · deducts 0.5 day
                      </p>
                    )}
                  </label>

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium">Reason</span>
                    <textarea
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      rows={3}
                      className="flex w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowApplyPanel(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1" disabled={submitting || !leaveTypeId || !startDate || !endDate}>
                      {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {leaveView === 'requests' && viewTab === 'mine' && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as LeaveStatus[]).map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={cn(
                  'rounded-2xl border p-3 text-left transition-all',
                  statusFilter === status
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card hover:bg-muted/50'
                )}
              >
                <p className="text-xs text-muted-foreground">{LEAVE_STATUS_LABELS[status]}</p>
                <p className="mt-0.5 text-xl font-bold">{statusCounts[status]}</p>
              </button>
            ))}
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {STATUS_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={cn(
                  'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-all',
                  statusFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border bg-card text-muted-foreground hover:bg-muted'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {leaveView === 'requests' && viewTab === 'team' && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {(['PENDING', 'APPROVED', 'REJECTED'] as LeaveStatus[]).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={cn(
                'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-all',
                statusFilter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border bg-card text-muted-foreground hover:bg-muted'
              )}
            >
              {LEAVE_STATUS_LABELS[status]}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {leaveView === 'calendar' ? null : loading ? (
        <LoadingState label="Loading leave requests…" />
      ) : list.length === 0 ? (
        <EmptyState
          icon={CalendarPlus}
          title={
            viewTab === 'team'
              ? 'No team leave requests'
              : statusFilter === 'ALL'
                ? 'No leave requests yet'
                : `No ${LEAVE_STATUS_LABELS[statusFilter]?.toLowerCase() ?? statusFilter.toLowerCase()} requests`
          }
          description={
            viewTab === 'team'
              ? 'Pending requests from your team will appear here.'
              : 'Submit a new request when you need time off.'
          }
          action={
            viewTab === 'mine' ? (
              <Button onClick={openApplyPanel}>
                Apply for leave
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="space-y-3">
          {list.map((req) => (
            <li key={req.id} ref={req.id === highlightId ? highlightRef : undefined}>
              <Card className={cn(req.id === highlightId && 'ring-2 ring-primary ring-offset-2')}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {viewTab === 'team' && req.employee?.fullName && (
                        <p className="text-sm font-medium text-primary">{req.employee.fullName}</p>
                      )}
                      <p className="font-semibold">{req.leaveType.displayName}</p>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {formatDate(req.startDate)} – {formatDate(req.endDate)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDays(req.totalDays, { working: true })}
                        {req.halfDay && req.halfDay !== 'FULL' && (
                          <Badge variant="info" className="ml-1.5">
                            {req.halfDay === 'FIRST_HALF' ? 'AM' : 'PM'}
                          </Badge>
                        )}
                      </p>
                    </div>
                    <Badge variant={LEAVE_STATUS_VARIANT[req.status as LeaveStatus] ?? 'secondary'}>
                      {LEAVE_STATUS_LABELS[req.status as SharedLeaveStatus] ?? req.status}
                    </Badge>
                  </div>
                  {req.reason && (
                    <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">
                      {req.reason}
                    </p>
                  )}
                  {(req.status === 'APPROVED' || req.status === 'REJECTED') && req.approvalDate && (
                    <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">
                      {req.status === 'APPROVED' ? 'Approved' : 'Rejected'} on{' '}
                      {formatDateTime(req.approvalDate)}
                      {req.approvedBy?.fullName ? ` by ${req.approvedBy.fullName}` : ''}
                    </p>
                  )}
                  {req.status === 'REJECTED' && req.rejectionReason && (
                    <p className="mt-2 text-sm text-destructive/90">{req.rejectionReason}</p>
                  )}
                  {viewTab === 'mine' && (req.status === 'PENDING' || req.status === 'APPROVED') && (
                    <div className="mt-3 flex gap-2 border-t border-border pt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionId === req.id}
                        onClick={() => handleCancel(req.id)}
                      >
                        {actionId === req.id ? 'Cancelling…' : 'Cancel leave'}
                      </Button>
                    </div>
                  )}
                  {viewTab === 'team' && req.status === 'PENDING' && (
                    <div className="mt-3 flex gap-2 border-t border-border pt-3">
                      <Button
                        size="sm"
                        disabled={actionId === req.id}
                        onClick={() => handleApprove(req.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionId === req.id}
                        onClick={() => handleReject(req.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
    </>
  );
}
